import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { Trash2Icon } from "lucide-react";

import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { DeleteTicketSchema, deleteTicketSchema } from "@/features/tickets/schemas";
import { deleteTicket } from "@/features/tickets/services/ticket";
import { Ticket } from "@/features/tickets/types/index";
import Error from "@/shared/components/Error";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface DeleteTicketFormProps {
  projectId: string;
  ticket: Ticket;
  permissions: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DeleteTicketForm({ projectId, ticket, open, onOpenChange }: DeleteTicketFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<DeleteTicketSchema>({
    resolver: zodResolver(deleteTicketSchema),
    defaultValues: {
      projectId: projectId,
      ticketId: ticket.id,
    },
  });

  const mutation = useMutation<undefined, AppError | Error, DeleteTicketSchema>({
    mutationFn: deleteTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_TICKETS, projectId], exact: false });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_BY_ID, projectId, ticket.id], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, projectId] });
      navigate(`/projects/${projectId}/tickets`);
    },
  });

  async function onSubmit(values: DeleteTicketSchema) {
    mutation.mutate(values);
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!mutation.isPending) {
          onOpenChange(newOpen);
        }
      }}
    >
      <AlertDialogContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete &ldquo;{ticket.key}&rdquo;?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the ticket and all its comments and history. This action <strong>cannot be undone</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {mutation.isError && <Error title="Unable to delete ticket" error={mutation.error} />}
            <Controller
              name="projectId"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input type="hidden" {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} />
                </Field>
              )}
            />
            <Controller
              name="ticketId"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input type="hidden" {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} />
                </Field>
              )}
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={mutation.isPending || mutation.isSuccess}>Cancel</AlertDialogCancel>
              <Button type="submit" variant="destructive" className="disabled:opacity-40" disabled={mutation.isPending || mutation.isSuccess}>
                <Trash2Icon />
                {mutation.isPending || mutation.isSuccess ? "Deleting ticket..." : "Delete ticket"}
              </Button>
            </AlertDialogFooter>
          </form>
        </FormProvider>
      </AlertDialogContent>
    </AlertDialog>
  );
}
